import React, { useEffect, useState } from 'react';
import { referralAdminAPI } from './api';

const safeError = (error, fallback) => (
  error.response?.data?.detail || fallback
);

export default function ReferralAdmin() {
  const [applications, setApplications] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState('');
  const [error, setError] = useState('');
  const [reviewAccess, setReviewAccess] = useState(null);
  const [newEvidenceReason, setNewEvidenceReason] = useState('incomplete');

  const load = async () => {
    setError('');
    try {
      const response = await referralAdminAPI.list();
      setApplications(response.data);
    } catch (requestError) {
      setError(safeError(requestError, 'Unable to load referral applications.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openApplication = async (referralId) => {
    setError('');
    setReviewAccess(null);
    try {
      const response = await referralAdminAPI.get(referralId);
      setSelected(response.data);
    } catch (requestError) {
      setError(safeError(requestError, 'Unable to load this referral application.'));
    }
  };

  const obtainReviewAccess = async () => {
    setAction('review');
    setError('');
    try {
      const response = await referralAdminAPI.createReviewAccess(selected.referral_id);
      setReviewAccess(response.data);
      await openApplication(selected.referral_id);
      setReviewAccess(response.data);
    } catch (requestError) {
      setError(safeError(requestError, 'Unable to open temporary evidence access.'));
    } finally {
      setAction('');
    }
  };

  const decide = async (decision, reasonCode) => {
    setAction(decision);
    setError('');
    setReviewAccess(null);
    try {
      const response = await referralAdminAPI.decide(
        selected.referral_id,
        decision,
        reasonCode
      );
      setSelected(response.data);
      await load();
    } catch (requestError) {
      setError(safeError(requestError, 'Unable to record the referral decision.'));
    } finally {
      setAction('');
    }
  };

  return (
    <section className="bg-gray-50 py-14 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        <p className="text-sm font-semibold uppercase tracking-wide text-orange-600">Administrator</p>
        <h1 className="mt-2 text-3xl font-bold text-blue-950">Referral review queue</h1>
        <p className="mt-3 text-gray-600">
          Minimal application metadata only. Evidence access is temporary and audited.
        </p>

        {error && <p className="mt-5 text-red-700" role="alert">{error}</p>}
        {loading && <p className="mt-6 text-gray-600" role="status">Loading pending referrals…</p>}

        {!loading && (
          <div className="mt-8 grid gap-6 md:grid-cols-2">
            <div className="space-y-3" aria-label="Pending referral applications">
              {applications.length === 0 && (
                <p className="rounded-lg border border-gray-200 bg-white p-4 text-gray-600">
                  No referral applications are pending review.
                </p>
              )}
              {applications.map((application) => (
                <button
                  type="button"
                  key={application.referral_id}
                  className="block w-full rounded-lg border border-gray-200 bg-white p-4 text-left hover:border-blue-700"
                  onClick={() => openApplication(application.referral_id)}
                >
                  <strong className="text-blue-950">
                    {application.applicant_name || 'Applicant unavailable'}
                  </strong>
                  <span className="mt-1 block text-sm text-gray-700">
                    {application.applicant_email || 'Email unavailable'}
                  </span>
                  <span className="mt-1 block text-sm text-gray-600">
                    Attempt {application.attempt_number} · {application.status.replace(/_/g, ' ')}
                  </span>
                  <span className="mt-1 block text-xs text-gray-500">
                    Submitted {application.submitted_at || 'Not submitted'}
                  </span>
                </button>
              ))}
            </div>

            {selected && (
              <article className="rounded-xl border border-gray-200 bg-white p-6">
                <h2 className="text-xl font-semibold text-blue-950">Application review</h2>
                <dl className="mt-4 text-sm">
                  <dt className="font-medium text-gray-500">Applicant</dt>
                  <dd className="text-gray-900">{selected.applicant_name || 'Unavailable'}</dd>
                  <dt className="mt-3 font-medium text-gray-500">Email</dt>
                  <dd className="text-gray-900">{selected.applicant_email || 'Unavailable'}</dd>
                  <dt className="mt-3 font-medium text-gray-500">Attempt</dt>
                  <dd className="text-gray-900">{selected.attempt_number}</dd>
                  <dt className="font-medium text-gray-500">Status</dt>
                  <dd className="text-gray-900">{selected.status.replace(/_/g, ' ')}</dd>
                  <dt className="mt-3 font-medium text-gray-500">Submitted</dt>
                  <dd className="text-gray-900">{selected.submitted_at || 'Not submitted'}</dd>
                </dl>

                {selected.status === 'needs_more_evidence' && (
                  <p className="mt-5 rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-950" role="status">
                    {selected.decision_reason_code === 'local_test_storage_reset'
                      ? 'Evidence is no longer available in local test storage. The applicant has been asked to upload it again.'
                      : 'Waiting for the applicant to submit replacement evidence.'}
                  </p>
                )}

                {['submitted', 'under_review'].includes(selected.status)
                  && !selected.evidence_available && (
                  <p className="mt-5 rounded-lg border border-red-200 bg-red-50 p-3 text-red-800" role="alert">
                    Evidence is unavailable. Review decisions are disabled.
                  </p>
                )}

                {['submitted', 'under_review'].includes(selected.status) && (
                  <div className="mt-6 flex flex-wrap gap-3">
                    <button
                      type="button"
                      className="gpj-btn gpj-btn--sm"
                      disabled={!selected.evidence_available || Boolean(action)}
                      onClick={obtainReviewAccess}
                    >
                      {action === 'review' ? 'Obtaining access…' : 'View evidence'}
                    </button>
                    {reviewAccess && (
                      <a
                        className="gpj-btn gpj-btn--primary gpj-btn--sm"
                        href={reviewAccess.review_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setReviewAccess(null)}
                      >
                        Open evidence in new tab
                      </a>
                    )}
                    <button
                      type="button"
                      className="gpj-btn gpj-btn--primary gpj-btn--sm"
                      disabled={!selected.evidence_available || Boolean(action)}
                      onClick={() => decide('approved', 'eligibility_verified')}
                    >
                      {action === 'approved' ? 'Approving…' : 'Approve'}
                    </button>
                    <label className="w-full text-sm font-medium text-blue-950" htmlFor="new-evidence-reason">
                      Reason for requesting another document
                      <select
                        id="new-evidence-reason"
                        className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2"
                        value={newEvidenceReason}
                        disabled={!selected.evidence_available || Boolean(action)}
                        onChange={(event) => setNewEvidenceReason(event.target.value)}
                      >
                        <option value="unreadable">Document is unreadable</option>
                        <option value="incomplete">Document is incomplete</option>
                        <option value="unsupported_document">Document is not supported</option>
                        <option value="other_document_required">Another document is required</option>
                      </select>
                    </label>
                    <button
                      type="button"
                      className="gpj-btn gpj-btn--sm"
                      disabled={!selected.evidence_available || Boolean(action)}
                      onClick={() => decide('needs_more_evidence', newEvidenceReason)}
                    >
                      {action === 'needs_more_evidence'
                        ? 'Requesting…'
                        : 'Request new evidence'}
                    </button>
                    <button
                      type="button"
                      className="gpj-btn gpj-btn--sm"
                      disabled={!selected.evidence_available || Boolean(action)}
                      onClick={() => decide('denied', 'unable_to_verify')}
                    >
                      {action === 'denied' ? 'Denying…' : 'Deny'}
                    </button>
                  </div>
                )}

                {['approved', 'denied'].includes(selected.status) && (
                  <p className="mt-5 rounded-lg bg-gray-50 p-3 text-gray-700" role="status">
                    Decision recorded: {selected.status}.
                  </p>
                )}
              </article>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
